import CollapsedCard from './CollpasedCard';
import RowCard from './RowCard';
import CardBack from './CardBack';
import CardOverlay from './CardOverlay';
import { useState, useRef } from 'react';
import TechnologySquare from './TechnologySquare';
import { SpecialTechTile, ColonyPill, WarBadge } from './SideTiles';
import BlueBank from './BlueBank';
import YellowBank from './YellowBank';
import { useImages } from '../context/ImageContext';
import CorruptionIcon from '../assets/images/corruption.gif';
import BreakdownTip from './BreakdownTip';
import UnhappyIcon from '../assets/images/unhappy.png';
import { IsMobile, useHover } from '../utils/Mobile';
import ActionBar, { BarTarget, CardAbility } from './ActionBar';
const { GameColors } = require('./Constants/Constants')
const { Overlay } = require('react-bootstrap');
const { AgeOrder, MilitaryCardTypes } = require('@tta/engine/Constants/Constants')

// The military hand in a fixed order: events first (territories are events: together, by age), then bonuses, tactics,
// aggressions, wars and pacts; each kind by age. Lists of the hand elsewhere (e.g. picking cards to discard) use it too.
const MilitaryHandOrder = [MilitaryCardTypes.Event, MilitaryCardTypes.Bonus, MilitaryCardTypes.Tactic,
    MilitaryCardTypes.Aggression, MilitaryCardTypes.War, MilitaryCardTypes.Pact];
const HandRank = (card) => MilitaryHandOrder.indexOf(card.type === MilitaryCardTypes.Territory ? MilitaryCardTypes.Event : card.type);
export const SortedMilitaryHand = (hand) => [...hand].sort((x, y) =>
    HandRank(x) - HandRank(y) || AgeOrder.indexOf(x.age) - AgeOrder.indexOf(y.age));

// Icon for each kind of special technology

// One status line: icon, current value, optionally "| +production", then anything else (e.g. resource pools)
function Indicator({icon, label, value, production, breakdown, children})
{
    return (
        <BreakdownTip className="tta-indicator" label={label} breakdown={breakdown}>
            <img src={icon} alt={label}/>
            <span>{value}</span>
            {production !== undefined && <span>| <span className={`tta-progress${production < 0 ? ' negative' : ''}`}>{production < 0 ? '−' : '+'}{Math.abs(production)}</span></span>}
            {children}
        </BreakdownTip>
    );
}

// Extra resources or science for military spending only, beside the counter: "+3 [resource]" (the hover says what
// for). They are not a discount: costs stay the same and these pay what they can, always first (the rules leave no choice).
// Civil: for civil spending (Cleopatra's wonders and urban buildings), shown apart from the red military ones
function MilitaryPools({Pools, Icon, Purpose, Civil = false})
{
    const total = Pools.reduce((sum, pool) => sum + pool.amount, 0);
    if (total === 0)
    {
        return null;
    }
    const sources = Pools.map(pool => `${pool.source}: ${pool.amount}`).join('\n');
    return (
        <span className={`tta-pool${Civil ? ' civil' : ''}`} title={`Extra for ${Purpose} this turn\n${sources}`}>
            +{total}<img src={Icon} alt={Purpose}/>
        </span>
    );
}

// The actions usable this turn as tokens. They are spent from the right, so active ones stay on the left.
// special: how many are out of the ordinary (Hammurabi's military action used as a civil action). They are spent last,
// so they are the leftmost, shown in parentheses.
// restricted: actions for some actions only (Gutenberg's, [{ source, purpose, used }]), after the others in parentheses;
// the current player can choose not to use it (UseRestricted, a checkbox beside it)
function ActionTokens({icon, label, total, remaining, breakdown, special = 0, restricted = [], UseRestricted = true, GameEngine, IsCurrentPlayer})
{
    return (
        <BreakdownTip className="tta-action-tokens" label={`${label}: ${remaining} of ${total} left`} breakdown={breakdown}>
            {Array.from({length: total}).map((_, index) => {
                const token = <img key={index} src={icon} alt={label} className={index >= remaining ? 'used' : undefined}/>;
                return index < special
                    ? <span key={index} className={`tta-special-token${index >= remaining ? ' used' : ''}`}>({token})</span>
                    : token;
            })}
            {restricted.map((action, index) =>
                <span key={`restricted ${index}`} className={`tta-restricted-token${action.used ? ' used' : ''}`}
                    title={`${action.source}: 1 civil action for ${action.purpose}, 1 science or resource cheaper; using it scores 2 culture${action.used ? ' (used this turn)' : ''}`}>
                    (<img src={icon} alt={`${label} (${action.source})`}/>)
                    {IsCurrentPlayer && !action.used &&
                        <input type="checkbox" checked={UseRestricted} onChange={(e) => GameEngine.setUseRestricted(e.target.checked)}
                            title={`Use ${action.source}'s civil action (and its discount) when it can be`}/>}
                </span>)}
        </BreakdownTip>
    );
}

// Face-down events a player prepared, as card backs (named on hover for whoever knows them), or a "none" sign
// Events prepared into the current or future events: as cards in a hand when the one looking knows them (their own),
// else face down, by age
function PreparedEvents({Title, Events})
{
    const known = Events.filter(event => event.card);
    const unknown = Events.filter(event => !event.card);
    return <div className="tta-prepared-events">
        <div className="tta-section-title">{Title}</div>
        {Events.length === 0 && <span className="tta-none" title="None">⦸</span>}
        {known.map(event => <CollapsedCard key={event.card.id} card={event.card}/>)}
        {unknown.length > 0 && <div className="tta-card-backs">
            {unknown.map((event, index) => <CardBack key={index} age={event.age} title={event.age}/>)}
        </div>}
    </div>;
}

// Completed wonders as pictures, then the unfinished one with the resource cost of each stage left to build.
// A wonder taken this turn is put back from its greyed copy in the card row, not from here.
function WondersBox({PlayerViewModel, Bar, GameEngine})
{
    const { images } = useImages();
    const inProgress = PlayerViewModel.wonderInProgress;
    return (
        <div className="tta-side-box" style={{ backgroundColor: '#decde4', flex: '2 0 auto', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
            {PlayerViewModel.wonders.map(wonder => <CardAbility key={wonder.id} Bar={Bar} On={`built:${wonder.name}`} GameEngine={GameEngine}>
                <CompletedWonder Wonder={wonder} image={images[wonder.image]}
                    Tokens={wonder.name === 'Louvre Museum' ? PlayerViewModel.louvreTokens : 0}/>
            </CardAbility>)}
            {inProgress && <BarTarget Bar={Bar} TargetKey="wonder" GameEngine={GameEngine}>
                <WonderInProgress key={inProgress.card.id} WonderInProgress={inProgress}
                    Stages={!Bar?.active ? (Bar?.wonderStages ?? []).map(option => option.stages) : []}
                    OnBuild={GameEngine?.BuildWonderStages}/>
            </BarTarget>}
        </div>
    );
}

// A completed wonder as its picture, with the full card shown on hover; Tokens: blue tokens kept on it (the Louvre
// Museum's), above it
function CompletedWonder({Wonder, image, Tokens = 0})
{
    const { images } = useImages();
    const [showOverlay, setShowOverlay, hoverProps] = useHover();
    const componentRef = useRef(null);
    return (
        <div>
            <div ref={componentRef} {...hoverProps}>
                {Tokens > 0 &&
                    <div className="tta-wonder-tokens" title={`${Tokens} blue token${Tokens === 1 ? '' : 's'} kept on it (each can be spent for 2 resources, and gives 1 culture)`}>
                        {Array.from({ length: Tokens }, (_, index) => <img key={index} src={images.BlueCube.src} alt="blue token"/>)}
                    </div>}
                {image
                    ? <img src={image.src} alt={Wonder.name} className={`tta-wonder-image${Wonder.crumbled ? ' crumbled' : ''}`} style={{ display: 'block' }}
                        title={Wonder.crumbled ? `Crumbled ${Wonder.name}` : undefined}/>
                    : (Wonder.crumbled ? `Crumbled ${Wonder.name}` : Wonder.name)}
            </div>
            <CardOverlay ShowOverlay={showOverlay} ComponentRef={componentRef} card={Wonder} callback={() => {}} OnClose={() => setShowOverlay(false)}/>
        </div>
    );
}

// The leader in play as a portrait, with the full card shown on hover; an empty slot when there is none
function LeaderSlot({Leader})
{
    const { images } = useImages();
    const [showOverlay, setShowOverlay, hoverProps] = useHover();
    const componentRef = useRef(null);
    if (!Leader)
    {
        return <div className="tta-leader-slot"/>;
    }
    const portrait = images[Leader.image];
    return (
        <div>
            <div className="tta-leader-slot" ref={componentRef} style={{ overflow: 'hidden' }}
                {...hoverProps}>
                {portrait ? <img src={portrait.src} alt={Leader.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : Leader.name}
            </div>
            <CardOverlay ShowOverlay={showOverlay} ComponentRef={componentRef} card={Leader} callback={() => {}} OnClose={() => setShowOverlay(false)}/>
        </div>
    );
}

// The unfinished wonder: name and remaining stage costs, with the full card shown on hover. With static buttons, Stages:
// how many stages can be built now (each a number of them, Masonry and the like allowing more than one): clicked, its
// next stage is built, or, clicked on a stage, it is built up to that one (OnBuild(stages))
function WonderInProgress({WonderInProgress, Stages = [], OnBuild})
{
    const { images } = useImages();
    const [showOverlay, setShowOverlay, hoverProps] = useHover();
    const componentRef = useRef(null);
    // (the stages that would be built, as many as hovered: over a stage, up to it; elsewhere on it, the next)
    const [upTo, setUpTo] = useState(null);
    const built = WonderInProgress.stagesBuilt;
    const reachable = (index) => Stages.includes(index - built + 1);
    const shown = upTo ?? (Stages.includes(1) ? 1 : 0);
    const [hovering, setHovering] = useState(false);
    return (
        <div>
            <div
                className={`tta-wonder-in-progress${Stages.length > 0 ? ' buildable' : ''}`}
                ref={componentRef}
                {...hoverProps}
                onMouseEnter={(event) => { hoverProps.onMouseEnter?.(event); setHovering(true); }}
                onMouseLeave={(event) => { hoverProps.onMouseLeave?.(event); setHovering(false); setUpTo(null); }}
                onClick={Stages.length > 0 ? () => OnBuild(upTo ?? 1) : hoverProps.onClick}>
                <div>{WonderInProgress.card.name}</div>
                {/* Each built stage is covered by its blue token, so the tokens on the wonder can be counted */}
                <div className="tta-wonder-stages">
                    {WonderInProgress.stageCosts.map((cost, index) => index < built
                        ? <img key={index} src={images.BlueCube.src} alt="built"
                            title={`Built${WonderInProgress.builtBy?.[index] ? ` by ${WonderInProgress.builtBy[index]}` : ''}: a blue token covers this stage`}/>
                        : <span key={index} className={`tta-wonder-stage${reachable(index) ? ' reachable' : ''}`}
                            onMouseEnter={reachable(index) ? () => setUpTo(index - built + 1) : undefined}
                            onMouseLeave={reachable(index) ? () => setUpTo(null) : undefined}>
                            {cost}
                            {hovering && index < built + shown &&
                                <img className="tta-wonder-preview" src={images.BlueCube.src} alt="would be built"/>}
                        </span>)}
                </div>
            </div>
            <CardOverlay
                ShowOverlay={showOverlay}
                ComponentRef={componentRef}
                card={WonderInProgress.card}
                callback={() => {}} //don't want to keep the overlay up
                OnClose={() => setShowOverlay(false)}
            />
        </div>
    );
}

// The pacts the civilization is in: each card, with a cube in each side's color (marked A / B for a one-sided pact)
// and what each side gets on hover
// The armies the player's units form for their tactic (full or obsolete)
const armyCount = (playerViewModel) => (playerViewModel.tacticArmies?.full ?? 0) + (playerViewModel.tacticArmies?.obsolete ?? 0);

// Bar, GameEngine: on the board of the player looking (static buttons), each pact is the action bar's target: clicked
// in the political phase, it is cancelled
function PactsBox({Pacts, PlayerColors, PlayerNames, Bar, GameEngine})
{
    if (!Pacts?.length)
    {
        return null;
    }
    return (
        <div className="tta-pacts" title="Pacts">
            {Pacts.map(pact =>
                // (as a tactic in play: the small card, the cubes on it along the bottom, the full card on hover)
                <div key={pact.card.id} className="tta-pact">
                    <BarTarget Bar={Bar} TargetKey={`card:${pact.card.id}`} GameEngine={GameEngine}><RowCard card={pact.card}/></BarTarget>
                    {/* Whose it is (who offered it: another pact they offer replaces it), in the top corner, on both sides' cards */}
                    <span className="tta-pact-owner" style={{ backgroundColor: PlayerColors[pact.offerer] }}
                        title={`${PlayerNames[pact.offerer]}'s pact (offered it): if ${PlayerNames[pact.offerer]} makes another pact, this one ends`}/>
                    <div className="tta-pact-cubes">
                        {[pact.a, pact.b].map((player, index) =>
                            <span key={player} className="tta-pact-cube" style={{ backgroundColor: PlayerColors[player] }}
                                title={`${pact.oneSided ? (index === 0 ? 'A: ' : 'B: ') : ''}${PlayerNames[player]}${pact.offerer === player ? ' (offered it)' : ''}: ${pact.terms[player]}`}>
                                {pact.oneSided ? (index === 0 ? 'A' : 'B') : ''}
                            </span>)}
                    </div>
                </div>)}
        </div>
    );
}

// The left column: status, leader and government, then the wonders / special technologies / colonies strip
// How the player's last turn stands, once the end of the game is known (see GameEnd.LastTurnStatus)
const LastTurnLabels = { 'last turn': 'Last turn', 'next round': 'Last turn next round', done: 'Last turn played' };

// Bar: the action bar's targets (the wonder being built)
function StatusBox({PlayerViewModel, GameEngine, IsCurrentPlayer, UseRestricted, Pacts, Wars, PlayerColors, PlayerNames, LastTurn, Resigned, Bar})
{
    const { images } = useImages();
    const breakdowns = PlayerViewModel.breakdowns;

    return (
        <div className="tta-pa-status" style={{ display: 'flex', gap: '6px', width: '450px', flex: '0 0 auto' }}>
            <div className="tta-box" style={{ flex: 1 }}>
                {(Resigned || LastTurn) &&
                    <div className={`tta-last-turn${Resigned || LastTurn === 'done' ? ' done' : ''}`}>{Resigned ? 'Resigned' : LastTurnLabels[LastTurn]}</div>}
                <div className="tta-status-bar">
                    {/* Wars this player is in (declared or declared on), until resolved */}
                    {(Wars ?? []).map((war, index) => {
                        const rival = war.aggressor === PlayerViewModel.playerNumber ? war.defender : war.aggressor;
                        return <WarBadge key={index} war={war} rival={PlayerNames[rival]} color={PlayerColors[rival]}/>;
                    })}
                    {PlayerViewModel.corruption > 0 &&
                        <img src={CorruptionIcon} alt="Corruption" title={`Corruption: lose ${PlayerViewModel.corruption} resources at the end of the turn`}/>}
                    {PlayerViewModel.famine > 0 &&
                        <span className="tta-famine" title={`Famine: ${PlayerViewModel.famine} food short at the end of the turn, losing ${PlayerViewModel.famine * 4} culture`}>
                            <img src={images.Food.src} alt="Famine"/>
                        </span>}
                    {/* Only an uprising is flagged here; discontent workers show on the yellow bank and worker pool */}
                    {PlayerViewModel.uprising &&
                        <img src={UnhappyIcon} alt="Uprising" style={{ width: '20px', height: '20px' }}
                            title={`Uprising: ${PlayerViewModel.discontentWorkers} discontent workers but only ${PlayerViewModel.workerPool} unused. Production will be skipped.`}/>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                    {/* Fixed width, with room for a resource pool, so the leader and government never move */}
                    <div className="tta-pa-indicators" style={{ flex: '0 0 130px' }}>
                        <Indicator icon={images.Culture.src} label="Culture" value={PlayerViewModel.culture} production={PlayerViewModel.cultureProduction} breakdown={breakdowns.culture}/>
                        <Indicator icon={images.Science.src} label="Science" value={PlayerViewModel.science} production={PlayerViewModel.scienceProduction} breakdown={breakdowns.science}>
                            <MilitaryPools Pools={PlayerViewModel.militarySciencePools} Icon={images.Science.src} Purpose="developing military unit technologies"/>
                        </Indicator>
                        <Indicator icon={images.Strength.src} label="Military strength" value={PlayerViewModel.militaryStrength} breakdown={breakdowns.military}/>
                        <Indicator icon={images.ExplorationResource.src} label="Colonization" value={PlayerViewModel.colonizationBonus || '-'} breakdown={breakdowns.colonization}/>
                        <BreakdownTip className="tta-indicator" label="Happy faces" breakdown={breakdowns.happyFaces}>
                            {PlayerViewModel.happyFaces === 0 ? '-' :
                                <span className="tta-happy-faces">{Array.from({length: PlayerViewModel.happyFaces}).map((_, index) =>
                                    <img key={index} src={images.Happiness.src} alt="Happy face"/>)}</span>}
                        </BreakdownTip>
                        <Indicator icon={images.Food.src} label="Food" value={PlayerViewModel.currentFood} production={PlayerViewModel.foodProduction} breakdown={breakdowns.food}/>
                        <Indicator icon={images.Resource.src} label="Resources" value={PlayerViewModel.currentResources} production={PlayerViewModel.resourceProduction} breakdown={breakdowns.resources}>
                            {/* (one above the other, to save width) */}
                            <span className="tta-pools">
                                <MilitaryPools Pools={PlayerViewModel.militaryPools} Icon={images.Resource.src} Purpose="building and upgrading military units"/>
                                <MilitaryPools Civil Pools={PlayerViewModel.wonderUrbanPools} Icon={images.Resource.src} Purpose="building wonders, and building and upgrading urban buildings"/>
                            </span>
                        </Indicator>
                    </div>
                    <div style={{ flex: 1 }}>
                        <CardAbility Bar={Bar} On="leader" GameEngine={GameEngine}>
                            <LeaderSlot Leader={PlayerViewModel.leader}/>
                        </CardAbility>
                        {/* Yellow tokens kept above the government card (Hippocrates), one for each new age */}
                        {PlayerViewModel.hippocratesTokens > 0 &&
                            <div className="tta-hippocrates" title={`Hippocrates: ${PlayerViewModel.hippocratesTokens} yellow token(s) above the government card; one goes into the yellow bank at each new age`}>
                                {Array.from({ length: PlayerViewModel.hippocratesTokens }, (_, index) => <img key={index} src={images.YellowCube.src} alt="yellow token"/>)}
                            </div>}
                        <div className="tta-government">{PlayerViewModel.government}</div>
                        <ActionTokens icon={images.CivilAction.src} label="Civil actions"
                            total={PlayerViewModel.civilActionCount} remaining={PlayerViewModel.civilActionsRemaining} breakdown={breakdowns.civilActions}
                            special={PlayerViewModel.militaryAsCivilActions} restricted={PlayerViewModel.restrictedCivilActions}
                            UseRestricted={UseRestricted} GameEngine={GameEngine} IsCurrentPlayer={IsCurrentPlayer}/>
                        <ActionTokens icon={images.MilitaryAction.src} label="Military actions"
                            total={PlayerViewModel.militaryActionCount} remaining={PlayerViewModel.militaryActionsRemaining} breakdown={breakdowns.militaryActions}/>
                        {/* The player's own tactics, as in the military area: the one they are on (only while it is theirs alone, just
                            played; a common one is in the military area, with their marker on it) with their marker, and those kept
                            beside it (replaced while Sun Tzu leads: no one can copy them until he is gone) small */}
                        {((PlayerViewModel.currentTactic && PlayerViewModel.tacticIsPrivate) || PlayerViewModel.keptTactics.length > 0) &&
                            <div className="tta-own-tactics">
                                {PlayerViewModel.currentTactic && PlayerViewModel.tacticIsPrivate &&
                                    <div className="tta-military-tactic">
                                        <RowCard card={PlayerViewModel.currentTactic}/>
                                        <div className="tta-tactic-markers">
                                            {/* (how many armies the units form for it: in the tooltip) */}
                                            <span className="tta-tactic-marker-wrap" title={`On this tactic: ${armyCount(PlayerViewModel)} arm${armyCount(PlayerViewModel) === 1 ? 'y' : 'ies'}`}>
                                                <span className="tta-tactic-marker" style={{ backgroundColor: PlayerColors[PlayerViewModel.playerNumber] }}/>
                                            </span>
                                        </div>
                                    </div>}
                                {PlayerViewModel.keptTactics.length > 0 &&
                                    <div className="tta-own-tactics-kept">
                                        {PlayerViewModel.keptTactics.map(tactic => <CollapsedCard key={tactic.id} card={tactic}/>)}
                                    </div>}
                            </div>}
                        <PactsBox Pacts={Pacts} PlayerColors={PlayerColors} PlayerNames={PlayerNames} Bar={Bar} GameEngine={GameEngine}/>
                    </div>
                </div>
            </div>
            <div className="tta-pa-side" style={{ width: '100px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <WondersBox PlayerViewModel={PlayerViewModel} Bar={Bar} GameEngine={GameEngine}/>
                <div className="tta-side-box" style={{ backgroundColor: '#bdc7e2', flex: '1 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    {PlayerViewModel.specialTechnologies.map(tech => <SpecialTechTile key={tech.name} tech={tech}/>)}
                </div>
                {/* Colonies, each as what it gives while held (its card on hover) */}
                <div className="tta-side-box" style={{ backgroundColor: '#cfda8b', flex: '1 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    {PlayerViewModel.colonies.map(colony => <ColonyPill key={colony.id} colony={colony}/>)}
                </div>
            </div>
        </div>
    );
}

// A player's tab: their name, and on hover a quick summary of their board (the status column's numbers), so it can be
// checked without opening the tab
export function PlayerTabTitle({PlayerViewModel})
{
    const { images } = useImages();
    const [show, setShow] = useState(false);
    const target = useRef(null);
    const p = PlayerViewModel;
    // (a phone has no hover: the summary is on the board itself, a tap away)
    if (IsMobile)
    {
        return <span className="tta-tab-name">{p.name}</span>;
    }
    const Line = ({icon, label, value, production}) =>
        <div className="tta-tab-summary-line">
            <img src={icon} alt={label} title={label}/>
            <span>{value}</span>
            {production !== undefined && <span>| <span className={`tta-progress${production < 0 ? ' negative' : ''}`}>{production < 0 ? '−' : '+'}{Math.abs(production)}</span></span>}
        </div>;
    return (
        <>
            <span ref={target} className="tta-tab-name" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>{p.name}</span>
            {/* (against the window, so it never makes the page taller: see CardOverlay) */}
            <Overlay show={show} target={target.current} placement="bottom-start" flip popperConfig={{ strategy: 'fixed' }}>
                {({ arrowProps, hasDoneInitialMeasure, popper, placement, show: _, ...props }) =>
                    <div {...props} className="tta-tab-summary">
                        <Line icon={images.Culture.src} label="Culture" value={p.culture} production={p.cultureProduction}/>
                        <Line icon={images.Science.src} label="Science" value={p.science} production={p.scienceProduction}/>
                        <Line icon={images.Strength.src} label="Military strength" value={p.militaryStrength}/>
                        <Line icon={images.ExplorationResource.src} label="Colonization" value={p.colonizationBonus || '-'}/>
                        <div className="tta-tab-summary-line">
                            {p.happyFaces === 0 ? <><img src={images.Happiness.src} alt="Happy faces"/><span>-</span></> :
                                <span className="tta-happy-faces">{Array.from({length: p.happyFaces}).map((_, index) =>
                                    <img key={index} src={images.Happiness.src} alt="Happy face"/>)}</span>}
                        </div>
                        <Line icon={images.Food.src} label="Food" value={p.currentFood} production={p.foodProduction}/>
                        <Line icon={images.Resource.src} label="Resources" value={p.currentResources} production={p.resourceProduction}/>
                    </div>}
            </Overlay>
        </>
    );
}

// ActionBar: the action bar, on the board of the player looking (null on the others), with TurnButtons (End turn, Undo…)
// and, in the political phase, Political (passing it) in place of its buttons. Military: the military area (everyone's
// strength and the tactics), the same on every board, at the top right
function PlayerArea({PlayerViewModel, GameEngine, IsCurrentPlayer, RetractableCardIds, UseRestricted, Pacts, Wars, PlayerColors, PlayerNames, LastTurn, Resigned, BoardSelection, ActionBar: Bar, TurnButtons, Political, Military})
{
    const { images } = useImages();
    // (the worker pool starts building while there is a worker in it and Build can be used, or is on)
    const build = Bar?.triggers.find(trigger => trigger.id === 'build');
    const buildFromPool = !!build?.enabled && PlayerViewModel.workerPool > 0 && (!Bar.active || build.active);
    // (a card in hand, with static buttons and no trigger on: what using it costs, and why it can't be used)
    const handOf = (card) => !Bar?.active ? Bar?.hand?.[`card:${card.id}`] : null;
    // (a military card, also while a trigger is on: its target's, e.g. an aggression's military actions while picked)
    const militaryOf = (card) => handOf(card) ?? (Bar?.active ? Bar.targets[`card:${card.id}`] : null);

    var getTechHeaderStyle = (techType) =>
    {
        return {
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '13px',
            textAlign: 'center',
            padding: '2px 6px',
            backgroundColor: GameColors.TechnologyColors[techType.replace(/\s/g, '')],
        }
    }

    return (
        <div key={PlayerViewModel.name} className="tta tta-pa" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            {/* The left column: the status, then (on the board of the player looking) the action bar under it */}
            <div style={{ flex: '0 0 450px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <StatusBox PlayerViewModel={PlayerViewModel} GameEngine={GameEngine} IsCurrentPlayer={IsCurrentPlayer} UseRestricted={UseRestricted}
                    Pacts={Pacts} Wars={Wars} PlayerColors={PlayerColors} PlayerNames={PlayerNames} LastTurn={LastTurn} Resigned={Resigned} Bar={Bar}/>
                {Bar && <div className="tta-box"><ActionBar Bar={Bar} GameEngine={GameEngine} TurnButtons={TurnButtons} Political={Political}/></div>}
            </div>
            <div className="tta-pa-main" style={{ flex: 1, minWidth: 0 }}>
                {/* The hands and the events played, compact, then (the same on every board) the military area at the right */}
                <div className="tta-pa-hands" style={{ display: 'flex', gap: '16px', marginBottom: '8px', alignItems: 'flex-start' }}>
                    {/* (each hand as wide as its longest card's name) */}
                    <div className="tta-left tta-pa-hand" style={{ width: 'max-content', minWidth: '150px' }}>
                        <div className="tta-section-title">Civil cards {PlayerViewModel.civilHand.length}/{PlayerViewModel.civilHandLimit}</div>
                        {PlayerViewModel.civilHand.map((card) => {
                            // (with static buttons a click on it does its action: it is put back from its copy in the card row)
                            const retractable = RetractableCardIds.includes(card.id);
                            return <BarTarget key={card.id} Bar={Bar} TargetKey={`card:${card.id}`} GameEngine={GameEngine}>
                                <CollapsedCard
                                    card={card}
                                    highlighted={retractable}
                                    title={retractable && !Bar ? 'Taken this turn; click to put it back' : undefined}
                                    onClick={retractable && !Bar ? () => GameEngine.retractCardTake(card.id) : undefined}
                                    Cost={handOf(card)?.cost} Usable={handOf(card)?.selectable} Note={handOf(card)?.reason}/>
                            </BarTarget>
                        })}
                    </div>
                    <div className="tta-left tta-pa-hand" style={{ width: 'max-content', minWidth: '150px' }}>
                        <div className="tta-section-title">Military cards: {PlayerViewModel.militaryHand.length}</div>
                        {/* Someone else's military cards are face down: only their backs show */}
                        <div className={PlayerViewModel.militaryHand.some(card => card.type === 'Hidden') ? 'tta-card-backs' : undefined}>
                            {SortedMilitaryHand(PlayerViewModel.militaryHand).map((card) => card.type === 'Hidden'
                                ? <CardBack key={card.id} age={card.age}/>
                                : <BarTarget key={card.id} Bar={Bar} TargetKey={`card:${card.id}`} GameEngine={GameEngine}>
                                    <CollapsedCard card={card} Cost={militaryOf(card)?.cost} Usable={militaryOf(card)?.selectable} Note={militaryOf(card)?.reason}/>
                                </BarTarget>)}
                        </div>
                    </div>
                    <div className="tta-left tta-pa-hand tta-pa-events" style={{ width: 'max-content', minWidth: '120px' }}>
                        <PreparedEvents Title="Current events played" Events={PlayerViewModel.eventsPlayed.current}/>
                    </div>
                    <div className="tta-left tta-pa-hand tta-pa-events" style={{ width: 'max-content', minWidth: '120px' }}>
                        <PreparedEvents Title="Future events played" Events={PlayerViewModel.eventsPlayed.future}/>
                    </div>
                    {Military && <div className="tta-pa-military">{Military}</div>}
                </div>
                <div className="tta-white-box">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                        <BreakdownTip style={{ flex: 1 }} label="Blue tokens owned" breakdown={PlayerViewModel.breakdowns.blueTokens}>
                            <BlueBank count={PlayerViewModel.blueBank} />
                        </BreakdownTip>
                        {/* (with static buttons, clicked to build: Build switched on, as its button does) */}
                        <div className={`tta-worker-pool${buildFromPool ? ' tta-pool-build' : ''}`} title={`${PlayerViewModel.workerPool} unused workers`}
                            onClick={buildFromPool ? () => GameEngine.StartTrigger('build') : undefined}>
                            <div>Worker pool</div>
                            <div>
                                {/* Discontent workers stand in the pool as unhappy faces */}
                                {Array.from({length: PlayerViewModel.workerPool}).map((_, index) => index < PlayerViewModel.discontentWorkers
                                    ? <img key={index} src={UnhappyIcon} alt="Discontent worker" title="Discontent worker"/>
                                    : <img key={index} src={images.Happiness.src} alt="Unused worker" title="Unused worker"/>)}
                                &nbsp;
                            </div>
                        </div>
                    </div>
                    <BreakdownTip label="Yellow tokens owned" breakdown={PlayerViewModel.breakdowns.yellowTokens}>
                        <YellowBank count={PlayerViewModel.yellowBank} HappyFaces={PlayerViewModel.happyFaces} PlayerNumber={PlayerViewModel.playerNumber}/>
                    </BreakdownTip>
                    {/* Buildings and units by age, newest age on top */}
                    <div className="tta-pa-grid">
                    <table className="tta-building-grid">
                        <tbody>
                            <tr>
                                <td className="tta-building-age"/>
                                {PlayerViewModel.techTypesUsed.map((techType) => {
                                    return <td style={getTechHeaderStyle(techType)} key={techType} >
                                        {techType}
                                    </td>
                                })}
                            </tr>
                            {PlayerViewModel.technologyGrid.map((techAge) => {
                                return <tr key={techAge.age}>
                                    <td className="tta-building-age">{techAge.age}</td>
                                    {techAge.Techs.map((tech,index) => {
                                        return <TechnologySquare key={index} Technology={tech}
                                            Selection={tech && BoardSelection?.cells[tech.name]}
                                            OnClick={(name) => GameEngine.ClickBoardCard(name)}
                                            OnUpgrade={tech && !BoardSelection && Bar?.upgradable?.includes(tech.name) ? GameEngine.StartUpgradeFrom : undefined}></TechnologySquare>
                                    })}
                                </tr>
                            })}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PlayerArea;